/**
 * ExpandableText Component
 * Displays text with "... More" / "Less" pattern for long content
 */

import { useState } from 'react';
import { Button, theme, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface ExpandableTextProps {
  text: string | null | undefined;
  maxLength?: number;
  emptyText?: string;
  /** Optional custom style for the text */
  textStyle?: React.CSSProperties;
}

const ExpandableText = ({
  text,
  maxLength = 100,
  emptyText = '-',
  textStyle,
}: ExpandableTextProps) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('common');
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) {
    return <Text type="secondary">{emptyText}</Text>;
  }

  const shouldTruncate = text.length > maxLength;
  const displayText = isExpanded || !shouldTruncate ? text : `${text.slice(0, maxLength)}...`;

  return (
    <span>
      <Text style={textStyle}>{displayText}</Text>
      {shouldTruncate && (
        <Button
          type="link"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          style={{
            padding: '0 4px',
            height: 'auto',
            fontSize: token.fontSizeSM,
            color: token.colorPrimary,
          }}
        >
          {isExpanded ? t('actions.less') : t('actions.more')}
        </Button>
      )}
    </span>
  );
};

export default ExpandableText;
